/**
 * [BLURY] | 🥭 output sizing
 * The output size is the ratio plus a user-set width. The preview is capped
 * independently so a 6000 px export never has to be re-rendered on every
 * slider tick.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import type { Ratio, Size } from './types';

/** The preview canvas never exceeds this on its long edge. */
export const PREVIEW_MAX_EDGE = 1200;

export const MIN_WIDTH = 64;
export const MAX_WIDTH = 6000;

export const WIDTH_PRESETS = [900, 1080, 1440, 2048] as const;

export const PRESET_RATIOS: Ratio[] = [
  { w: 3, h: 4 },
  { w: 2, h: 3 },
  { w: 4, h: 5 },
  { w: 9, h: 16 },
  { w: 1, h: 1 },
  { w: 4, h: 3 },
  { w: 16, h: 9 },
  { w: 21, h: 9 },
];

export function normaliseRatio(w: number, h: number): Ratio {
  return {
    w: Math.max(0.25, Number(w) || 1),
    h: Math.max(0.25, Number(h) || 1),
  };
}

export function clampWidth(width: number): number {
  if (!Number.isFinite(width)) return MIN_WIDTH;
  return Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width)));
}

/** Full output size: the width the user asked for, height from the ratio. */
export function outputSize(ratio: Ratio, width: number): Size {
  const w = clampWidth(width);
  return { width: w, height: Math.max(1, Math.round(w / (ratio.w / ratio.h))) };
}

/**
 * Preview size for an output size — scaled down to fit PREVIEW_MAX_EDGE, never
 * scaled up. `scale` is how much bigger the export is than the preview, and is
 * what the renderer multiplies resolution-bound values by.
 */
export function previewSizeFor(size: Size): { size: Size; scale: number } {
  const longEdge = Math.max(size.width, size.height);
  if (longEdge <= PREVIEW_MAX_EDGE) return { size, scale: 1 };
  const k = PREVIEW_MAX_EDGE / longEdge;
  const preview = {
    width: Math.max(1, Math.round(size.width * k)),
    height: Math.max(1, Math.round(size.height * k)),
  };
  return { size: preview, scale: size.width / preview.width };
}
